import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Checkbox,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FilterOutlined, MoreOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { canDeleteOpportunity } from '../../utils/documentDeletePolicy';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { useAuthStore } from '@/store/authStore';
import {
  OpportunityRegisterRow,
  OpportunitySavedView,
} from '@/services/opportunity.service';
import {
  useCreateOpportunitySavedView,
  useDeleteOpportunity,
  useDeleteOpportunitySavedView,
  useExportOpportunityRegister,
  useOpportunityRegister,
  useOpportunityRegisterStats,
  useOpportunitySavedViews,
} from '@/hooks/useOpportunities';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { OPPORTUNITY_KEYS } from '@/hooks/useOpportunities';
import { OpportunitiesKanbanBoard } from '@/components/crm/opportunities/OpportunitiesKanbanBoard';
import { Segmented } from 'antd';
import { OPPORTUNITY_STAGES, canonicalOpportunityStage } from '@/constants/opportunity-stages';

const { Title, Text } = Typography;
const STATUSES = ['Open', 'Won', 'Lost', 'Abandoned'];

type ColumnKey =
  | 'opportunity_number'
  | 'opportunity_name'
  | 'customer_name'
  | 'stage'
  | 'status'
  | 'estimated_value'
  | 'probability'
  | 'expected_revenue'
  | 'next_action'
  | 'next_action_date'
  | 'aging_days'
  | 'expected_close_date'
  | 'sales_owner'
  | 'sales_team_name'
  | 'actions';

const defaultColumns: ColumnKey[] = [
  'opportunity_number',
  'opportunity_name',
  'customer_name',
  'stage',
  'status',
  'estimated_value',
  'probability',
  'expected_revenue',
  'next_action',
  'next_action_date',
  'aging_days',
  'sales_team_name',
  'actions',
];

function parseCsvParam(v: string | null): string[] | undefined {
  if (!v) return undefined;
  const x = v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return x.length ? x : undefined;
}

export const OpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const { baseCurrency } = useCurrency();
  const sym =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    (baseCurrency?.currency_code ?? '');

  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState<'register' | 'pipeline'>('register');
  const [visibleCols, setVisibleCols] = useState<ColumnKey[]>(defaultColumns);
  const [savedViewName, setSavedViewName] = useState('');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 300);

  const registerFilters = useMemo(() => {
    const page = Number(searchParams.get('page') || 1);
    const pageSize = Number(searchParams.get('pageSize') || 25);
    return {
      page,
      pageSize,
      search: debouncedSearch || undefined,
      stage: parseCsvParam(searchParams.get('stage')),
      status: parseCsvParam(searchParams.get('status')),
      sales_owner: parseCsvParam(searchParams.get('sales_owner')),
      industry: parseCsvParam(searchParams.get('industry')),
      opportunity_type: parseCsvParam(searchParams.get('opportunity_type')),
      project_type: parseCsvParam(searchParams.get('project_type')),
      probabilityMin: searchParams.get('probabilityMin') ? Number(searchParams.get('probabilityMin')) : undefined,
      probabilityMax: searchParams.get('probabilityMax') ? Number(searchParams.get('probabilityMax')) : undefined,
      minValue: searchParams.get('minValue') ? Number(searchParams.get('minValue')) : undefined,
      maxValue: searchParams.get('maxValue') ? Number(searchParams.get('maxValue')) : undefined,
      agingBucket: (searchParams.get('agingBucket') as any) || undefined,
      hasNextAction: searchParams.get('hasNextAction') ? searchParams.get('hasNextAction') === 'true' : undefined,
      sales_team_id: searchParams.get('sales_team_id') || undefined,
      missing_team: searchParams.get('missing_team') === 'true',
      sortBy: searchParams.get('sortBy') || 'created_at',
      sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
    };
  }, [searchParams, debouncedSearch]);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (debouncedSearch === urlSearch) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, [debouncedSearch]);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (searchInput.trim() === debouncedSearch && urlSearch !== searchInput) {
      setSearchInput(urlSearch);
    }
  }, [searchParams]);

  const { data: registerData, isLoading } = useOpportunityRegister(registerFilters);
  const { data: statsData } = useOpportunityRegisterStats(registerFilters);
  const { data: savedViews = [] } = useOpportunitySavedViews();
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });
  const deleteOpp = useDeleteOpportunity();
  const exportMutation = useExportOpportunityRegister();
  const createSavedView = useCreateOpportunitySavedView();
  const deleteSavedView = useDeleteOpportunitySavedView();

  const { isExcelBusy, uploadPct, downloadTemplate, downloadExport, handleImport } = useExcelMigrationIo({
    templateUrl: '/crm/opportunities/excel/template',
    exportUrl: '/crm/opportunities/excel/export',
    importUrl: '/crm/opportunities/excel/import',
    templateFilename: 'opportunities-import-template.xlsx',
    exportFilenamePrefix: 'opportunities',
    onImportSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.pipeline() });
      queryClient.invalidateQueries({ queryKey: [...OPPORTUNITY_KEYS.all, 'register'] });
      queryClient.invalidateQueries({ queryKey: [...OPPORTUNITY_KEYS.all, 'register-stats'] });
    },
    buildExportParams: () => {
      const f = registerFilters;
      const p: Record<string, string | number | boolean | undefined> = {};
      const multi = (key: string, vals?: string[]) => {
        if (vals?.length) p[key] = vals.join(',');
      };
      multi('stage', f.stage);
      multi('status', f.status);
      multi('sales_owner', f.sales_owner);
      multi('industry', f.industry);
      multi('opportunity_type', f.opportunity_type);
      multi('project_type', f.project_type);
      if (f.search) p.search = f.search;
      if (f.probabilityMin !== undefined) p.probabilityMin = f.probabilityMin;
      if (f.probabilityMax !== undefined) p.probabilityMax = f.probabilityMax;
      if (f.minValue !== undefined) p.minValue = f.minValue;
      if (f.maxValue !== undefined) p.maxValue = f.maxValue;
      if (f.agingBucket) p.agingBucket = f.agingBucket;
      if (f.hasNextAction !== undefined) p.hasNextAction = f.hasNextAction;
      if (f.sales_team_id) p.sales_team_id = f.sales_team_id;
      if (f.missing_team) p.missing_team = true;
      if (f.sortBy) p.sortBy = f.sortBy;
      if (f.sortOrder) p.sortOrder = f.sortOrder;
      return p;
    },
  });

  const rows = registerData?.items || [];
  const summary = registerData?.summary;

  const updateFilter = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    });
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (page: number, pageSize: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    next.set('pageSize', String(pageSize));
    setSearchParams(next);
  };

  const handleDelete = (opp: OpportunityRegisterRow) => {
    Modal.confirm({
      title: 'Delete opportunity',
      content: `Delete "${opp.opportunity_name}"?`,
      okType: 'danger',
      onOk: () => deleteOpp.mutateAsync(opp.id),
    });
  };

  const applySavedView = (view: OpportunitySavedView) => {
    const filters = (view.filters_json || {}) as Record<string, unknown>;
    const sort = (view.sort_json || {}) as Record<string, unknown>;
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) next.set(k, v.map(String).join(','));
      else if (v !== null && v !== undefined && String(v) !== '') next.set(k, String(v));
    });
    if (sort.sortBy) next.set('sortBy', String(sort.sortBy));
    if (sort.sortOrder) next.set('sortOrder', String(sort.sortOrder));
    next.set('page', '1');
    next.set('pageSize', '25');
    setSearchParams(next);
    setSearchInput(typeof filters.search === 'string' ? filters.search : '');
    const cols = (view.columns_json as any)?.visibleCols;
    if (Array.isArray(cols) && cols.length) setVisibleCols(cols as ColumnKey[]);
  };

  const saveCurrentView = async () => {
    if (!savedViewName.trim()) return;
    const filters: Record<string, unknown> = {};
    ['search', 'stage', 'status', 'sales_owner', 'industry', 'opportunity_type', 'project_type', 'probabilityMin', 'probabilityMax', 'minValue', 'maxValue', 'agingBucket', 'hasNextAction'].forEach((k) => {
      const v = searchParams.get(k);
      if (!v) return;
      filters[k] = v.includes(',') ? v.split(',') : v;
    });
    await createSavedView.mutateAsync({
      name: savedViewName.trim(),
      filters_json: filters,
      sort_json: { sortBy: registerFilters.sortBy, sortOrder: registerFilters.sortOrder },
      columns_json: { visibleCols },
      is_shared: false,
    });
    setSavedViewName('');
  };

  const fmtCurrency = (v: unknown) => `${sym}${Number(v || 0).toLocaleString('en-US')}`;

  const allColumns: ColumnsType<OpportunityRegisterRow> = [
    {
      title: 'Opportunity #',
      dataIndex: 'opportunity_number',
      key: 'opportunity_number',
      width: 150,
      fixed: 'left',
      render: (v, r) => <a onClick={() => navigate(`/growth/opportunities/${r.id}`)}>{v}</a>,
    },
    { title: 'Name', dataIndex: 'opportunity_name', key: 'opportunity_name', width: 220 },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', width: 180 },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      width: 130,
      render: (v: string) => {
        const stage = canonicalOpportunityStage(v);
        return <Tag color={stage === 'Won' ? 'green' : stage === 'Lost' ? 'red' : 'blue'}>{stage}</Tag>;
      },
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110 },
    { title: 'Est. Value', dataIndex: 'estimated_value', key: 'estimated_value', width: 140, render: fmtCurrency },
    { title: 'Probability', dataIndex: 'probability', key: 'probability', width: 110, render: (v) => `${v || 0}%` },
    { title: 'Weighted', dataIndex: 'expected_revenue', key: 'expected_revenue', width: 140, render: fmtCurrency },
    { title: 'Next Action', dataIndex: 'next_action', key: 'next_action', width: 220, ellipsis: true },
    {
      title: 'Next Action Date',
      dataIndex: 'next_action_date',
      key: 'next_action_date',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-'),
    },
    { title: 'Aging (days)', dataIndex: 'aging_days', key: 'aging_days', width: 120 },
    {
      title: 'Expected Close',
      dataIndex: 'expected_close_date',
      key: 'expected_close_date',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'Owner',
      dataIndex: 'sales_owner',
      key: 'sales_owner',
      width: 150,
      render: (v: string) => resolveUserDisplay(v, 'Unknown user'),
    },
    {
      title: 'Sales team',
      dataIndex: 'sales_team_name',
      key: 'sales_team_name',
      width: 160,
      render: (v: string) => v || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, row) => {
        const deleteCheck = canDeleteOpportunity(row);
        return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'view', icon: <EyeOutlined />, label: 'View', onClick: () => navigate(`/growth/opportunities/${row.id}`) },
              { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/growth/opportunities/${row.id}/edit`) },
              { type: 'divider' as const },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                disabled: !deleteCheck.ok,
                onClick: () => {
                  if (!deleteCheck.ok) return;
                  handleDelete(row);
                },
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
        );
      },
    },
  ];

  const columns = allColumns.filter((c) => visibleCols.includes((c.key as ColumnKey) || 'actions'));

  return (
    <div>
      {uploadPct !== null && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Uploading import file…</Text>
          <Progress percent={uploadPct} size="small" />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Opportunity Register</Title>
          <Text type="secondary">Advanced sales register with analytics, saved views, and export.</Text>
        </div>
        <Space wrap>
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode(v as 'register' | 'pipeline')}
            options={[
              { label: 'Register', value: 'register' },
              { label: 'Pipeline', value: 'pipeline' },
            ]}
          />
          <Button icon={<FilterOutlined />} onClick={() => setShowFilters((p) => !p)}>{showFilters ? 'Hide Filters' : 'Show Filters'}</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
            Template
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
            Export Excel
          </Button>
          <Upload accept=".xlsx,.xls" showUploadList={false} customRequest={handleImport} disabled={isExcelBusy}>
            <Button icon={<UploadOutlined />} loading={isExcelBusy}>
              Import
            </Button>
          </Upload>
          <Button icon={<DownloadOutlined />} loading={exportMutation.isPending} onClick={() => exportMutation.mutate(registerFilters)}>Export CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/opportunities/new')}>Add Opportunity</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Total opportunities" value={summary?.total_count || 0} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Open opportunities" value={summary?.open_count || 0} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Weighted value" value={summary?.weighted_value || 0} prefix={sym} formatter={(v) => Number(v).toLocaleString('en-US')} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card><Statistic title="Overdue next actions" value={summary?.overdue_next_actions || 0} /></Card></Col>
      </Row>

      {!!statsData && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Conversion snapshot — Won: ${statsData.conversion.won}, Lost: ${statsData.conversion.lost}, Open: ${statsData.conversion.open}`}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Select
              allowClear
              placeholder="Apply saved view"
              style={{ width: 260 }}
              options={savedViews.map((v) => ({ value: v.id, label: `${v.name}${v.is_shared ? ' (shared)' : ''}` }))}
              onChange={(id) => {
                const view = savedViews.find((x) => x.id === id);
                if (view) applySavedView(view);
              }}
            />
            <Input
              value={savedViewName}
              onChange={(e) => setSavedViewName(e.target.value)}
              style={{ width: 220 }}
              placeholder="New saved view name"
            />
            <Button icon={<SaveOutlined />} onClick={saveCurrentView} loading={createSavedView.isPending}>Save View</Button>
            <Select
              allowClear
              placeholder="Delete saved view"
              style={{ width: 220 }}
              options={savedViews.filter((v) => v.user_id === user?.id).map((v) => ({ value: v.id, label: v.name }))}
              onChange={(id) => id && deleteSavedView.mutate(String(id))}
            />
          </Space>
          <Checkbox.Group
            options={allColumns.map((c) => ({ label: String(c.title), value: c.key as ColumnKey }))}
            value={visibleCols}
            onChange={(vals) => setVisibleCols(vals as ColumnKey[])}
          />
        </Space>
      </Card>

      {showFilters && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Input
                allowClear
                value={searchInput}
                placeholder="Search opportunity/customer/contact"
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Col>
            <Col xs={24} md={8}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Stage"
                value={parseCsvParam(searchParams.get('stage'))}
                options={OPPORTUNITY_STAGES.map((x) => ({ label: x, value: x }))}
                onChange={(v) => updateFilter({ stage: v.length ? v.join(',') : undefined })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Status"
                value={parseCsvParam(searchParams.get('status'))}
                options={STATUSES.map((x) => ({ label: x, value: x }))}
                onChange={(v) => updateFilter({ status: v.length ? v.join(',') : undefined })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Input
                allowClear
                placeholder="Industry (comma separated)"
                value={searchParams.get('industry') || ''}
                onChange={(e) => updateFilter({ industry: e.target.value })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Input
                allowClear
                placeholder="Opportunity type"
                value={searchParams.get('opportunity_type') || ''}
                onChange={(e) => updateFilter({ opportunity_type: e.target.value })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Input
                allowClear
                placeholder="Project type"
                value={searchParams.get('project_type') || ''}
                onChange={(e) => updateFilter({ project_type: e.target.value })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Sales team"
                value={searchParams.get('sales_team_id') || undefined}
                options={salesTeams.map((t) => ({ label: t.team_name, value: t.id }))}
                onChange={(v) => updateFilter({ sales_team_id: v || undefined, missing_team: undefined })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Team assignment"
                value={
                  searchParams.get('missing_team') === 'true'
                    ? 'missing'
                    : searchParams.get('sales_team_id')
                      ? 'team'
                      : undefined
                }
                options={[
                  { label: 'Missing / unassigned team', value: 'missing' },
                ]}
                onChange={(v) => {
                  if (v === 'missing') {
                    const next = new URLSearchParams(searchParams);
                    next.delete('sales_team_id');
                    next.set('missing_team', 'true');
                    next.set('page', '1');
                    setSearchParams(next);
                  } else {
                    updateFilter({ missing_team: undefined });
                  }
                }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {viewMode === 'pipeline' ? (
        <Card title="Opportunity pipeline">
          <OpportunitiesKanbanBoard />
        </Card>
      ) : (
      <Card>
        <Spin spinning={isExcelBusy}>
          <Table<OpportunityRegisterRow>
            rowKey="id"
            loading={isLoading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1600 }}
          pagination={{
            current: registerData?.page || registerFilters.page || 1,
            pageSize: registerData?.pageSize || registerFilters.pageSize || 25,
            total: registerData?.total || 0,
            showSizeChanger: true,
          }}
          onChange={(pagination, _filters, sorter: any) => {
            setPage(pagination.current || 1, pagination.pageSize || 25);
            if (sorter?.field) {
              const order = sorter.order === 'ascend' ? 'asc' : 'desc';
              const next = new URLSearchParams(searchParams);
              next.set('sortBy', sorter.field);
              next.set('sortOrder', order);
              setSearchParams(next);
            }
          }}
        />
        </Spin>
      </Card>
      )}
    </div>
  );
};
